import desktopStyles from "./toolbox-poster-size.module.css";
import mobileStyles from "./toolbox-poster-size-mobile.module.css";
import { Toolbox, BasicToolboxProps } from "../../toolbox";
import { ToolboxComponentPosterSizeIcon } from "../../toolbox-components/toolbox-component-poster-size-icon";
import { ToolboxComponentToggle } from "../../toolbox-components/toolbox-component-toggle";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { SizePresetDropdownMenu } from "PosterEditor/model/size-presets/size-preset-dropdown-menu";
import { SizePresetInlineMenu } from "PosterEditor/model/size-presets/size-preset-inline-menu";
import {
  SizePreset,
  useSizePresets,
} from "PosterEditor/model/size-presets/size-preset";
import { MAX_PHYSICAL_SIZE_MM } from "PosterEditor/model/size-presets/size-preset-utils";

import { memo, useMemo, useCallback, useEffect, useState } from "react";

interface ToolboxPosterSize2Props extends BasicToolboxProps {
  variant?: 'desktop' | 'mobile';
}

type SizeUnit = "MM" | "CM" | "INCH" | "Pixels";

// Conversion constants
const MM_PER_INCH = 25.4;
const MM_PER_CM = 10;
const MAX_PIXELS = 2560;

// Unit limits (in their respective units)
const UNIT_LIMITS = {
  MM: { min: 160, max: 500 }, // ~4-16 inches
  CM: { min: 16, max: 50 }, // ~4-16 inches
  INCH: { min: 6, max: 20 },
  Pixels: { min: 1000, max: Infinity },
};

const ToolboxPosterSize2Component = (props: ToolboxPosterSize2Props) => {
  const { variant = 'desktop' } = props;
  const isMobile = variant === 'mobile';
  const styles = isMobile ? mobileStyles : desktopStyles;

  const {
    project,
    setSizePresetID,
    setOrientation: setProjectOrientation,
  } = usePosterProject();
  const {
    sizePresets: posterSizePresets,
    sizePresetByID,
    isLoading,
    isError,
  } = useSizePresets();
  const presetID = project.sizeOption.presetID;

  const isCustomPreset = useMemo(() => {
    if (presetID) {
      const preset = sizePresetByID(presetID);
      if (!preset) return false;
      return preset.heightInMM === 0 || preset.widthInMM === 0;
    }
    return false;
  }, [presetID, sizePresetByID]);

  // State for unit selection and dimensions
  const [selectedUnit, setSelectedUnit] = useState<SizeUnit>("Pixels");
  const [inputWidth, setInputWidth] = useState<string>("");
  const [inputHeight, setInputHeight] = useState<string>("");

  // Conversion functions: Physical units to pixels
  const physicalToPixels = useCallback(
    (
      width: number,
      height: number,
      unit: SizeUnit
    ): { width: number; height: number } => {
      if (unit === "Pixels") {
        return { width, height };
      }

      // Convert to MM first
      let widthMM = width;
      let heightMM = height;
      if (unit === "CM") {
        widthMM = width * MM_PER_CM;
        heightMM = height * MM_PER_CM;
      } else if (unit === "INCH") {
        widthMM = width * MM_PER_INCH;
        heightMM = height * MM_PER_INCH;
      }

      // Calculate aspect ratio and scale so largest side is MAX_PIXELS
      const largestSide = Math.max(widthMM, heightMM);
      const scale = MAX_PIXELS / largestSide;

      return {
        width: Math.round(widthMM * scale),
        height: Math.round(heightMM * scale),
      };
    },
    []
  );

  // Conversion functions: Pixels to physical units
  const pixelsToPhysical = useCallback(
    (
      widthPX: number,
      heightPX: number,
      unit: SizeUnit
    ): { width: number; height: number } => {
      if (unit === "Pixels") {
        return { width: widthPX, height: heightPX };
      }

      // Calculate what the dimensions would be if the largest side is MAX_PIXELS
      const largestSide = Math.max(widthPX, heightPX);
      const scale = largestSide / MAX_PIXELS;

      // Get dimensions in MM (reverse the scaling)
      const widthMM = (widthPX / largestSide) * MAX_PHYSICAL_SIZE_MM;
      const heightMM = (heightPX / largestSide) * MAX_PHYSICAL_SIZE_MM;

      // Convert from MM to target unit
      if (unit === "CM") {
        return {
          width: Math.round(widthMM / MM_PER_CM),
          height: Math.round(heightMM / MM_PER_CM),
        };
      } else if (unit === "INCH") {
        return {
          width: Math.round(widthMM / MM_PER_INCH),
          height: Math.round(heightMM / MM_PER_INCH),
        };
      } else {
        // MM
        return {
          width: Math.round(widthMM),
          height: Math.round(heightMM),
        };
      }
    },
    []
  );

  // Initialize custom dimensions from project
  useEffect(() => {
    if (isCustomPreset) {
      const widthPX = project.sizeOption.options.widthPX;
      const heightPX = project.sizeOption.options.heightPX;
      if (widthPX && heightPX) {
        if (selectedUnit === "Pixels") {
          setInputWidth(widthPX.toString());
          setInputHeight(heightPX.toString());
        } else {
          const dimensions = pixelsToPhysical(widthPX, heightPX, selectedUnit);
          setInputWidth(dimensions.width.toString());
          setInputHeight(dimensions.height.toString());
        }
      }
    }
  }, [
    isCustomPreset,
    project.sizeOption.options.widthPX,
    project.sizeOption.options.heightPX,
    selectedUnit,
    pixelsToPhysical,
  ]);

  const onSelectPreset = useCallback(
    (preset: SizePreset) => {
      const isCustom =
        preset.widthInMM === 0 ||
        preset.widthInMM === null ||
        preset.widthInMM === undefined;
      setSizePresetID(
        preset.id,
        isCustom
          ? { width: project.sizeOption.options.widthPX ?? 2560, height: 2560 }
          : undefined
      );
    },
    [
      setSizePresetID,
      project.sizeOption.options.widthPX,
      project.sizeOption.options.heightPX,
    ]
  );

  const onSelectOrientationIndex = useCallback(
    (index: number) => {
      const orientation = index === 0 ? "portrait" : "landscape";
      setProjectOrientation(orientation);
    },
    [setProjectOrientation]
  );

  const selectedOrientationIndex = useMemo(
    () => (project.sizeOption.options.orientation === "portrait" ? 0 : 1),
    [project.sizeOption.options.orientation]
  );

  const selectedPreset = useMemo(() => {
    if (!presetID) {
      return undefined;
    }
    return sizePresetByID(presetID);
  }, [sizePresetByID, presetID]);

  // Handler for width input
  const onWidthChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setInputWidth(value);
    },
    []
  );

  // Handler for height input
  const onHeightChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setInputHeight(value);
    },
    []
  );

  // Handler for width blur - validate and update project
  const onWidthBlur = useCallback(() => {
    const numValue = parseFloat(inputWidth);
    const limits = UNIT_LIMITS[selectedUnit];

    if (isNaN(numValue)) {
      // Reset to project value if invalid
      const widthPX = project.sizeOption.options.widthPX;
      const heightPX = project.sizeOption.options.heightPX;
      if (widthPX && heightPX) {
        if (selectedUnit === "Pixels") {
          setInputWidth(widthPX.toString());
        } else {
          const dimensions = pixelsToPhysical(widthPX, heightPX, selectedUnit);
          setInputWidth(dimensions.width.toString());
        }
      }
      return;
    }

    // Clamp to unit limits
    const clampedValue = Math.max(limits.min, Math.min(limits.max, numValue));
    setInputWidth(clampedValue.toString());

    // Update project with new dimensions if both values are valid
    if (presetID && inputHeight) {
      const heightNum = parseFloat(inputHeight);
      if (!isNaN(heightNum)) {
        const pixels =
          selectedUnit === "Pixels"
            ? { width: clampedValue, height: heightNum }
            : physicalToPixels(clampedValue, heightNum, selectedUnit);
        setSizePresetID(presetID, pixels);
      }
    }
  }, [
    inputWidth,
    inputHeight,
    selectedUnit,
    presetID,
    project.sizeOption.options.widthPX,
    project.sizeOption.options.heightPX,
    setSizePresetID,
    physicalToPixels,
    pixelsToPhysical,
  ]);

  // Handler for height blur - validate and update project
  const onHeightBlur = useCallback(() => {
    const numValue = parseFloat(inputHeight);
    const limits = UNIT_LIMITS[selectedUnit];

    if (isNaN(numValue)) {
      // Reset to project value if invalid
      const widthPX = project.sizeOption.options.widthPX;
      const heightPX = project.sizeOption.options.heightPX;
      if (widthPX && heightPX) {
        if (selectedUnit === "Pixels") {
          setInputHeight(heightPX.toString());
        } else {
          const dimensions = pixelsToPhysical(widthPX, heightPX, selectedUnit);
          setInputHeight(dimensions.height.toString());
        }
      }
      return;
    }

    // Clamp to unit limits
    const clampedValue = Math.max(limits.min, Math.min(limits.max, numValue));
    setInputHeight(clampedValue.toString());

    // Update project with new dimensions if both values are valid
    if (presetID && inputWidth) {
      const widthNum = parseFloat(inputWidth);
      if (!isNaN(widthNum)) {
        const pixels =
          selectedUnit === "Pixels"
            ? { width: widthNum, height: clampedValue }
            : physicalToPixels(widthNum, clampedValue, selectedUnit);
        setSizePresetID(presetID, pixels);
      }
    }
  }, [
    inputHeight,
    inputWidth,
    selectedUnit,
    presetID,
    project.sizeOption.options.widthPX,
    project.sizeOption.options.heightPX,
    setSizePresetID,
    physicalToPixels,
    pixelsToPhysical,
  ]);

  // Handler for Enter key on width input
  const onWidthKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.currentTarget.blur();
      }
    },
    []
  );

  // Handler for Enter key on height input
  const onHeightKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.currentTarget.blur();
      }
    },
    []
  );

  // Handler for unit selection change
  const onSelectUnitIndex = useCallback(
    (index: number) => {
      const units: SizeUnit[] = ["MM", "CM", "INCH", "Pixels"];
      const newUnit = units[index];
      setSelectedUnit(newUnit);

      // Convert current pixel dimensions to new unit
      const widthPX = project.sizeOption.options.widthPX;
      const heightPX = project.sizeOption.options.heightPX;
      if (widthPX && heightPX) {
        if (newUnit === "Pixels") {
          setInputWidth(widthPX.toString());
          setInputHeight(heightPX.toString());
        } else {
          const dimensions = pixelsToPhysical(widthPX, heightPX, newUnit);
          setInputWidth(dimensions.width.toString());
          setInputHeight(dimensions.height.toString());
        }
      }
    },
    [
      project.sizeOption.options.widthPX,
      project.sizeOption.options.heightPX,
      pixelsToPhysical,
    ]
  );

  const selectedUnitIndex = useMemo(() => {
    const units: SizeUnit[] = ["MM", "CM", "INCH", "Pixels"];
    return units.indexOf(selectedUnit);
  }, [selectedUnit]);

  const isSquare = useMemo(() => {
    if (selectedPreset) {
      return selectedPreset.widthInMM === selectedPreset.heightInMM;
    }
    return false;
  }, [selectedPreset]);

  if (isLoading) {
    return (
      <Toolbox
        title="Size"
        collapsible={props.collapsible}
        isCollapsed={props.isCollapsed}
        onToggleCollapse={props.onToggleCollapse}
      >
        <div className={styles.container}>
          <div className={styles.contentWrapper}>
            <div>Loading size options...</div>
          </div>
        </div>
      </Toolbox>
    );
  }

  if (isError) {
    return (
      <Toolbox
        title="Size"
        collapsible={props.collapsible}
        isCollapsed={props.isCollapsed}
        onToggleCollapse={props.onToggleCollapse}
      >
        <div className={styles.container}>
          <div className={styles.contentWrapper}>
            <div>Error loading size options. Please try again.</div>
          </div>
        </div>
      </Toolbox>
    );
  }

  return (
    <Toolbox
      title="Size"
      collapsible={props.collapsible}
      isCollapsed={props.isCollapsed}
      onToggleCollapse={props.onToggleCollapse}
    >
      <div className={styles.container}>
        <div className={styles.contentWrapper}>
          <div className={styles.shippingNotice}>
            {isMobile ? 'UK shipping only' : (
              <>
                We currently only ship <br />
                within the United Kingdom
              </>
            )}
          </div>
          <div className={styles.selectedSizeIconContainer}>
            <ToolboxComponentPosterSizeIcon
              project={project}
              isSelected={true}
              onClick={undefined}
            />
          </div>
          {isMobile ? (
            <SizePresetInlineMenu
              sizePresets={posterSizePresets}
              selectedPreset={selectedPreset}
              onSelectPreset={onSelectPreset}
            />
          ) : (
            <SizePresetDropdownMenu
              sizePresets={posterSizePresets}
              selectedPreset={selectedPreset}
              onSelectPreset={onSelectPreset}
            />
          )}
          {!isCustomPreset && !isSquare && (
            <ToolboxComponentToggle
              optionTitles={["Portrait", "Landscape"]}
              selectedOptionIndex={selectedOrientationIndex}
              onClickOption={onSelectOrientationIndex}
            />
          )}
          {isCustomPreset && (
            <div className={styles.customDimensionsContainer}>
              <ToolboxComponentToggle
                optionTitles={["MM", "CM", "INCH", "Pixels"]}
                selectedOptionIndex={selectedUnitIndex}
                onClickOption={onSelectUnitIndex}
              />
              <div className={styles.helperText}>
                {selectedUnit === "Pixels"
                  ? `Enter dimensions (min ${UNIT_LIMITS[selectedUnit].min}px)`
                  : `Enter dimensions between ${UNIT_LIMITS[selectedUnit].min}-${
                      UNIT_LIMITS[selectedUnit].max
                    }${selectedUnit.toLowerCase()}`}
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="custom-width">
                  Width (
                  {selectedUnit === "Pixels" ? "px" : selectedUnit.toLowerCase()})
                </label>
                <input
                  id="custom-width"
                  type="number"
                  min={UNIT_LIMITS[selectedUnit].min}
                  max={
                    selectedUnit === "Pixels"
                      ? undefined
                      : UNIT_LIMITS[selectedUnit].max
                  }
                  value={inputWidth}
                  onChange={onWidthChange}
                  onBlur={onWidthBlur}
                  onKeyDown={onWidthKeyDown}
                  className={styles.dimensionInput}
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="custom-height">
                  Height (
                  {selectedUnit === "Pixels" ? "px" : selectedUnit.toLowerCase()})
                </label>
                <input
                  id="custom-height"
                  type="number"
                  min={UNIT_LIMITS[selectedUnit].min}
                  max={
                    selectedUnit === "Pixels"
                      ? undefined
                      : UNIT_LIMITS[selectedUnit].max
                  }
                  value={inputHeight}
                  onChange={onHeightChange}
                  onBlur={onHeightBlur}
                  onKeyDown={onHeightKeyDown}
                  className={styles.dimensionInput}
                />
              </div>
            </div>
          )}
        </div>
        {selectedPreset && selectedPreset.priceInt && !isCustomPreset && (
          <div className={styles.priceSection}>
            <div className={styles.priceRow}>
              <div className={styles.priceLabelGroup}>
                <span className={styles.priceLabel}>
                  {selectedPreset.sizeDescription}
                </span>
                {selectedPreset.materialDescription && (
                  <span className={styles.priceMaterial}>
                    {selectedPreset.materialDescription}
                  </span>
                )}
              </div>
              <span className={styles.priceValue}>
                £{(selectedPreset.priceInt / 100).toFixed(2)}
              </span>
            </div>
            <div className={styles.shippingIncluded}>Shipping included</div>
          </div>
        )}
      </div>
    </Toolbox>
  );
};

export const ToolboxPosterSize2 = memo(ToolboxPosterSize2Component);
